
import React from 'react';
import Badge from '../base/Badge';

interface Procurement {
  id: number;
  title: string;
  city: string;
  category: string;
  value_band: string;
  value_estimate: string;
  planned_competition_start: string;
  planned_competition_end: string;
  status: string;
  updated_at: string;
}

interface ProcurementTableProps {
  procurements: Procurement[];
  onViewDetails: (id: number) => void;
  sortBy: string;
  onSort: (field: string) => void;
}

export default function ProcurementTable({ 
  procurements, 
  onViewDetails, 
  sortBy, 
  onSort 
}: ProcurementTableProps) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const getValueBandVariant = (band: string) => {
    const variants: { [key: string]: 'value-i' | 'value-ii' | 'value-iii' | 'value-iv' } = {
      'I': 'value-i',
      'II': 'value-ii',
      'III': 'value-iii',
      'IV': 'value-iv'
    };
    return variants[band] || 'default';
  };

  const getSortIcon = (field: string) => {
    if (sortBy === `${field}_asc`) {
      return <i className="ri-arrow-up-line text-sm w-4 h-4 flex items-center justify-center ml-1" aria-hidden="true"></i>;
    }
    if (sortBy === `${field}_desc`) {
      return <i className="ri-arrow-down-line text-sm w-4 h-4 flex items-center justify-center ml-1" aria-hidden="true"></i>;
    }
    return <i className="ri-arrow-up-down-line text-sm w-4 h-4 flex items-center justify-center ml-1 opacity-50" aria-hidden="true"></i>;
  };

  const handleSort = (field: string) => {
    if (sortBy === `${field}_asc`) {
      onSort(`${field}_desc`);
    } else {
      onSort(`${field}_asc`);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <button
                onClick={() => handleSort('title')}
                className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Hankinta
                {getSortIcon('title')}
              </button>
            </th>
            <th className="px-6 py-3 text-left">
              <button
                onClick={() => handleSort('city')}
                className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Kaupunki
                {getSortIcon('city')}
              </button>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Kategoria
            </th>
            <th className="px-6 py-3 text-left">
              <button
                onClick={() => handleSort('value_band')}
                className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Arvoluokka
                {getSortIcon('value_band')}
              </button>
            </th>
            <th className="px-6 py-3 text-left">
              <button
                onClick={() => handleSort('competition')}
                className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Kilpailutus
                {getSortIcon('competition')}
              </button>
            </th>
            <th className="px-6 py-3 text-left">
              <button
                onClick={() => handleSort('updated')}
                className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Päivitetty
                {getSortIcon('updated')}
              </button>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tila
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {procurements.map((procurement) => (
            <tr
              key={procurement.id}
              onClick={() => onViewDetails(procurement.id)}
              className="hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900 hover:text-blue-600">
                  {procurement.title}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-700">
                {procurement.city}
              </td>
              <td className="px-6 py-4 text-sm text-gray-700">
                {procurement.category}
              </td>
              <td className="px-6 py-4">
                <Badge variant={getValueBandVariant(procurement.value_band)}>
                  {procurement.value_band} - {procurement.value_estimate}
                </Badge>
              </td>
              <td className="px-6 py-4 text-sm text-gray-700">
                <div className="flex items-center">
                  <i className="ri-calendar-line mr-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                  {formatDate(procurement.planned_competition_start)} - {formatDate(procurement.planned_competition_end)}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-700">
                {formatDate(procurement.updated_at)}
              </td>
              <td className="px-6 py-4">
                <Badge variant="status">
                  {procurement.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {procurements.length === 0 && (
        <div className="text-center py-12 bg-white border-t border-gray-200">
          <i className="ri-search-line text-gray-400 text-4xl mb-4 w-16 h-16 flex items-center justify-center mx-auto" aria-hidden="true"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ei osumia valituilla suodattimilla
          </h3>
          <p className="text-gray-600 mb-4">
            Kokeile muuttaa hakuehtoja tai tyhjentää suodattimet
          </p>
        </div>
      )}
    </div>
  );
}
